import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PencilSimple, WarningCircle } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * BrandSettingsSummary
 * ─────────────────────
 * The reusable "pulled from your Brand Profile" primitive used by every
 * content-creation tool under the Zero Friction mandate. Renders a quiet
 * row of chips (brand name, industry, tone, audience, primary color)
 * with a single "Edit profile →" deep link.
 *
 * If the brand_profile is missing critical fields it surfaces a soft
 * nudge — never a blocker. Tools should still let the user generate.
 *
 * Props
 *  - fields:   subset of chips to show, e.g. ['brand','industry','tone']
 *              defaults to a sensible set.
 *  - onLoad:   callback(profile) — fired once the profile is fetched so
 *              the parent tool can pre-fill its own inputs.
 *  - compact:  smaller padding for inline placement next to a single input.
 */
const DEFAULT_FIELDS = ['brand', 'industry', 'tone', 'audience', 'color'];

const labelFor = (k, p) => {
  switch (k) {
    case 'brand':    return p?.brand_name || null;
    case 'industry': return p?.industry || null;
    case 'tone':     return p?.tone_of_voice || null;
    case 'audience': return (p?.target_audience || '').split(/[,.]/)[0].trim().slice(0, 32) || null;
    case 'voice':    return Array.isArray(p?.voice_samples) && p.voice_samples.filter(Boolean).length
                            ? `${p.voice_samples.filter(Boolean).length} voice samples` : null;
    case 'website':  return p?.website || null;
    default:         return null;
  }
};

const chipStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '3px 9px', borderRadius: 999, fontSize: 10.5,
  letterSpacing: '0.02em', fontFamily: 'ui-monospace, monospace',
  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)',
  color: 'rgba(245,240,232,0.6)', whiteSpace: 'nowrap',
};

const BrandSettingsSummary = ({ fields = DEFAULT_FIELDS, onLoad, compact = false }) => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/user/profile`);
        if (!alive) return;
        const p = data?.brand_profile || {};
        setProfile(p);
        const incomplete = !p.brand_name || !p.industry || !p.tone_of_voice;
        setMissing(incomplete);
        if (onLoad) onLoad(p);
      } catch {
        if (!alive) return;
        setMissing(true);
        if (onLoad) onLoad({});
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const primaryColor = profile?.brand_colors?.primary || profile?.brand_colors?.[0];

  return (
    <div data-testid="brand-settings-summary"
      style={{
        display: 'flex', alignItems: 'center', flexWrap: 'wrap',
        gap: 8, padding: compact ? '6px 0' : '10px 12px',
        borderRadius: 10,
        background: compact ? 'transparent' : 'rgba(255,255,255,0.015)',
        border: compact ? 'none' : '1px dashed rgba(255,255,255,0.07)',
      }}>
      <span style={{
        fontFamily: 'ui-monospace, monospace', fontSize: 9.5,
        letterSpacing: '0.12em', textTransform: 'uppercase',
        color: 'rgba(245,240,232,0.32)',
      }}>
        From your Brand Profile
      </span>

      {fields.map((k) => {
        if (k === 'color') {
          if (!primaryColor) return null;
          return (
            <span key="color" style={chipStyle} data-testid="brand-summary-color">
              <span style={{ width: 8, height: 8, borderRadius: 999, background: primaryColor, display: 'inline-block' }} />
              {primaryColor}
            </span>
          );
        }
        const v = labelFor(k, profile);
        if (!v) return null;
        return (
          <span key={k} style={chipStyle} data-testid={`brand-summary-${k}`}>{v}</span>
        );
      })}

      {missing && (
        <span style={{ ...chipStyle, background: 'rgba(255,170,90,0.06)', borderColor: 'rgba(255,170,90,0.18)', color: '#ffaa5a' }}>
          <WarningCircle size={11} weight="fill" />
          Profile incomplete
        </span>
      )}

      <button onClick={() => navigate('/dashboard/settings')}
        data-testid="brand-summary-edit"
        style={{
          marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4,
          background: 'transparent', border: 'none', cursor: 'pointer',
          fontFamily: 'ui-monospace, monospace', fontSize: 10,
          letterSpacing: '0.08em', textTransform: 'uppercase',
          color: '#c8f060', fontWeight: 700, padding: 0,
        }}>
        <PencilSimple size={11} weight="bold" /> Edit profile
      </button>
    </div>
  );
};

export default BrandSettingsSummary;
